import { useEffect, useRef, useState } from 'react';
import { apiUrl } from '../lib/api';
import { authHeaders } from '../lib/session';

const REASONS_AR = ['رقم خاطئ', 'فائز خاطئ', 'معلومة مختلقة', 'بيانات قديمة', 'أخرى'];
const REASONS_EN = ['Wrong number', 'Wrong winner', 'Hallucinated', 'Out of date', 'Other'];
// API expects EN values regardless of UI language
const REASON_API_VALUES = ['Wrong number', 'Wrong winner', 'Hallucinated', 'Out of date', 'Other'];

const T = {
  title: { ar: 'ابلاغ عن مشكلة في الإجابة', en: 'Report an issue with this answer' },
  commentLabel: { ar: 'وصف المشكلة (≤ 50 كلمة)', en: 'Describe the issue (≤ 50 words)' },
  reasonLabel: { ar: 'نوع المشكلة', en: "What's wrong?" },
  cancel: { ar: 'إلغاء', en: 'Cancel' },
  submit: { ar: 'إرسال', en: 'Submit' },
  submitting: { ar: 'جارٍ الإرسال...', en: 'Submitting...' },
  success: { ar: 'تم الإرسال — شكراً', en: 'Submitted — thanks' },
  error: { ar: 'حدث خطأ، جرب مرة أخرى', en: 'Something went wrong, try again' },
};

export type ReportModalProps = {
  question: string;
  answer: string;
  lang: 'ar' | 'en';
  onClose: () => void;
  onSuccess: () => void;
};

function countWords(s: string): number {
  const trimmed = s.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

export function ReportModal({ question, answer, lang, onClose, onSuccess }: ReportModalProps) {
  const [comment, setComment] = useState('');
  const [reasonIdx, setReasonIdx] = useState(REASONS_EN.length - 1); // Other
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !submitting) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  const reasons = lang === 'ar' ? REASONS_AR : REASONS_EN;
  const wordCount = countWords(comment);
  const valid = wordCount >= 1 && wordCount <= 50;

  async function submit() {
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await fetch(apiUrl('/api/report-answer'), {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders({ 'content-type': 'application/json' }),
        body: JSON.stringify({
          question,
          answer,
          comment: comment.trim(),
          reason: REASON_API_VALUES[reasonIdx],
        }),
      });
      if (!r.ok) throw new Error(`${r.status}`);
      onSuccess();
    } catch {
      setError(T.error[lang]);
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-tatweer-navy/70 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      dir={lang === 'ar' ? 'rtl' : 'ltr'}
    >
      <div
        className="relative bg-white rounded-2xl shadow-card-lg ring-1 ring-tatweer-line p-6 max-w-md w-full mx-4 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-bold text-tatweer-navy">{T.title[lang]}</h2>

        <div className="space-y-2">
          <label className="block text-xs text-tatweer-body-soft">{T.reasonLabel[lang]}</label>
          <select
            value={reasonIdx}
            onChange={(e) => setReasonIdx(parseInt(e.target.value, 10))}
            disabled={submitting}
            className="block w-full rounded-xl bg-white ring-1 ring-tatweer-line px-3 py-2 text-sm text-tatweer-body focus:outline-none focus:ring-2 focus:ring-tatweer-navy"
          >
            {reasons.map((r, i) => (
              <option key={i} value={i}>{r}</option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="block text-xs text-tatweer-body-soft">{T.commentLabel[lang]}</label>
            <span className={`text-[11px] tabular-nums ${wordCount > 50 ? 'text-rose-600 font-semibold' : 'text-tatweer-muted'}`}>
              {wordCount} / 50
            </span>
          </div>
          <textarea
            ref={textareaRef}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={submitting}
            rows={4}
            className="block w-full rounded-xl bg-white ring-1 ring-tatweer-line px-3 py-2 text-sm text-tatweer-body resize-none focus:outline-none focus:ring-2 focus:ring-tatweer-navy"
            dir={lang === 'ar' ? 'rtl' : 'ltr'}
          />
        </div>

        {error && <div className="text-rose-600 text-xs">{error}</div>}

        <div className={`flex gap-2 pt-2 ${lang === 'ar' ? 'justify-start' : 'justify-end'}`}>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-xl text-sm text-tatweer-body bg-tatweer-light hover:bg-white ring-1 ring-tatweer-line disabled:opacity-50"
          >
            {T.cancel[lang]}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!valid || submitting}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-tatweer-navy hover:bg-tatweer-navy-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? T.submitting[lang] : T.submit[lang]}
          </button>
        </div>
      </div>
    </div>
  );
}
